/**
 * <cart-recommendations>
 * One "You may also need" block under the cart items, loaded automatically.
 * The pinned products (rendered in <template data-pinned-cells>) come first.
 * Then for every cart product it fetches complementary + related recommendations
 * (sections/cart-item-recommendations.liquid), merges them round-robin so each
 * cart item contributes, drops duplicates and products already in the cart, and
 * tops the list up with the fixed fallback collection when there aren't enough.
 * Fetched fragments are cached per URL, so the cart re-render after a quantity
 * change or an add-to-cart doesn't refetch what it already has.
 * Reuses the innerHTML-swap pattern so injected cards (quick-add / quick-view)
 * upgrade correctly.
 */
if (!customElements.get('cart-recommendations')) {
  const fragmentCache = new Map();
  const MAX_SOURCE_PRODUCTS = 6;

  customElements.define(
    'cart-recommendations',
    class CartRecommendations extends HTMLElement {
      connectedCallback() {
        this.load();
      }

      async load() {
        const grid = this.querySelector('.cart-recommendations__grid');
        const base = this.dataset.baseUrl;
        const limit = parseInt(this.dataset.limit, 10) || 8;
        const cartIds = (this.dataset.productIds || '').split(',').filter(Boolean);
        if (!grid || !base) return;

        this.classList.add('is-loading');
        try {
          const seen = new Set(cartIds);
          const picked = [];
          const take = (cell) => {
            const id = cell.dataset.productId;
            if (!id || seen.has(id) || picked.length >= limit) return;
            seen.add(id);
            picked.push(cell);
          };

          const pinned = this.querySelector('template[data-pinned-cells]');
          if (pinned) [...pinned.content.querySelectorAll('.cart-item-recommendations__cell')].forEach(take);

          // Skip the recommendation requests when the pinned products already fill the block.
          const sources = picked.length < limit ? cartIds.slice(0, MAX_SOURCE_PRODUCTS) : [];
          const lists = await Promise.all(
            sources.map(async (id) => {
              const url = `${base}&product_id=${id}&limit=${limit}`;
              const [complementary, related] = await Promise.all([
                this.fetchCells(`${url}&intent=complementary`),
                this.fetchCells(`${url}&intent=related`),
              ]);
              return complementary.concat(related);
            })
          );

          const longest = Math.max(0, ...lists.map((list) => list.length));
          for (let i = 0; i < longest && picked.length < limit; i++) {
            lists.forEach((list) => list[i] && take(list[i]));
          }

          if (picked.length < limit && this.dataset.fallbackUrl) {
            (await this.fetchCells(this.dataset.fallbackUrl)).forEach(take);
          }

          this.classList.remove('is-loading');
          if (!picked.length) {
            this.hidden = true;
            return;
          }
          grid.innerHTML = picked.map((cell) => cell.outerHTML).join('');
          this.classList.add('is-loaded');
        } catch (e) {
          // Suggestions are optional: hide the block rather than leave a spinner.
          this.classList.remove('is-loading');
          this.hidden = true;
        }
      }

      async fetchCells(url) {
        if (!fragmentCache.has(url)) {
          fragmentCache.set(
            url,
            fetch(url)
              .then((response) => (response.ok ? response.text() : ''))
              .catch(() => '')
          );
        }
        const text = await fragmentCache.get(url);
        const fragment = new DOMParser().parseFromString(text, 'text/html').querySelector('.cart-item-recos-fragment');
        if (!fragment) return [];
        return [...fragment.querySelectorAll('.cart-item-recommendations__cell')].filter((cell) =>
          cell.querySelector('.product-card')
        );
      }
    }
  );
}
